import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { AlertOnErrors } from '../../shared/alertOnErrors';
import { Background } from '../shared/background/Background';
import { Banner } from '../shared/Banner';
import { MainContainer } from '../shared/MainContainer';
import { StickyToolbar } from '../shared/StickyToolbar';
import './cookiePolicyPage.scss';

/**
 * Cookie Policy Page.
 */
export const CookiePolicyPage = () => {

    const { t } = useTranslation();

    // a formatted table with a lower case alpha in the first column and the item in the second column
    // presents a a pseudo ordered list
    const LowerAlphaList = useCallback((props: {
        listItems: object[],
    }) => {
        const { listItems } = props;
        const alphaCharacters: Array<string> = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'y'];

        return (

            <table className="without-borders">
                <tbody>
                    {
                        listItems.map((item, index) => {
                            return (
                                <tr key={index}>
                                    <td>{alphaCharacters[index]})</td>
                                    <td>{item}</td>
                                </tr>
                            );
                        })
                    }
                </tbody>
            </table>
        );

    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    return (
        <Background>
            <Banner fluid>
                <StickyToolbar>
                    <Row>
                        <Col xs={11} md="">
                            <h1>{t("cookiePolicyPage.title", "Cookie Policy")}</h1>
                        </Col>

                    </Row>
                </StickyToolbar>
            </Banner>

            <MainContainer className="cookie-policy" fluid>
                <AlertOnErrors errors={null} />
                <div className="text-container">

                    <p>{t("cookiePolicyPage.background.paragraph.1", "Our website uses cookies to distinguish you from other users of our website. This helps us to provide you with a good experience when you browse our website and also allows us to improve our site.")}</p>
                    <p>{t("cookiePolicyPage.background.paragraph.2", "A cookie is a small file of letters and numbers that we store on your browser or the hard drive of your computer if you agree. Cookies contain information that is transferred to your computer's hard drive.")}</p>
                    <p>{t("cookiePolicyPage.background.paragraph.3", "We use the following cookies:")}
                        <p>&#x2022; <b>{t("cookiePolicyPage.background.paragraph.3.1.strictlyNecessaryCookies", "Strictly necessary cookies.")}</b> {t("cookiePolicyPage.background.paragraph.3.1", "These are cookies that are required for the operation of our website. They include, for example, cookies that enable you to log into secure areas of our website, use a shopping cart or make use of e-billing services.")}</p>
                        <p>&#x2022; <b>{t("cookiePolicyPage.background.paragraph.3.2.analyticalOrPerformanceCookies", "Analytical or performance cookies.")}</b> {t("cookiePolicyPage.background.paragraph.3.2", "These allow us to recognise and count the number of visitors and to see how visitors move around our website when they are using it. This helps us to improve the way our website works, for example, by ensuring that users are finding what they are looking for easily.")}</p>
                        <p>&#x2022; <b>{t("cookiePolicyPage.background.paragraph.3.3.functionalityCookies", "Functionality Cookies.")}</b> {t("cookiePolicyPage.background.paragraph.3.3", "These are used to recognise you when you return to our website. This enables us to personalise our content for you, greet you by name and remember your preferences (for example, your choice of language or region).")}</p>
                        <p>&#x2022; <b>{t("cookiePolicyPage.background.paragraph.3.4.targetingCookies", "Targeting Cookies.")}</b> {t("cookiePolicyPage.background.paragraph.3.4", "These cookies record your visit to our website, the pages you have visited and the links you have followed. We will use this information to make our website and the advertising displayed on it more relevant to your interests. We may also share this information with third parties for this purpose.")}</p>
                    </p>
                    <p>{t("cookiePolicyPage.background.paragraph.4", "You can find more information about the individual cookies we use and the purposes for which we use them in the table below:")}</p>

                    <table className="with-borders">
                        <thead>
                            <tr>
                                <th>{t("privacyPolicyPage.paragraph4.tableHeader.1.2", "Cookie Name")}</th>
                                <th>{t("privacyPolicyPage.paragraph4.tableHeader.2", "Purpose")}</th>
                                <th>{t("privacyPolicyPage.paragraph4.tableHeader.3", "More information")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{t("privacyPolicyPage.paragraph4.tableRow1.1", ".AspNetCore.Identity.Application")}</td>
                                <td>{t("privacyPolicyPage.paragraph4.tableRow1.2", "Used to identify the logged in user")}</td>
                                <td rowSpan={3} >
                                    <p>{t("privacyPolicyPage.paragraph4.tableRow1.3", "These cookies are essential for our site to OR enables us to:")}</p>

                                    <LowerAlphaList listItems={[
                                        t("privacyPolicyPage.paragraph4.tableRow1.3.a", "Estimate our audience size and usage pattern."),
                                        t("privacyPolicyPage.paragraph4.tableRow1.3.b", "Store information about your preferences, and so allow us to customise our site and to provide you with offers that are targeted to your individual interests."),
                                        t("privacyPolicyPage.paragraph4.tableRow1.3.c", "Speed up your searches."),
                                        t("privacyPolicyPage.paragraph4.tableRow1.3.d", "Recognise you when you return to our site."),
                                        t("privacyPolicyPage.paragraph4.tableRow1.3.e", "Allow you to use our site in a way that makes your browsing experience more convenient, for example, by allowing you to store items in an electronic shopping basket between visits. If you register with us or complete our online forms, we will use cookies to remember your details during your current visit, and any future visits provided the cookie was not deleted in the interim.")
                                    ]} />

                                </td>
                            </tr>
                            <tr>
                                <td>{t("privacyPolicyPage.paragraph4.tableRow2.1", "idsrv.session")}</td>
                                <td>{t("privacyPolicyPage.paragraph4.tableRow2.2", "Used to attach the logged in user to an individual session.")}</td>
                                
                            </tr>
                            <tr>
                                <td>{t("privacyPolicyPage.paragraph4.tableRow3.1", "ARRAffinity and ARRAffinitySameSite")}</td>
                                <td>{t("privacyPolicyPage.paragraph4.tableRow3.1", "Used to optimise the routing of the user across multiple web servers in the server farm behind the website.")}</td>
                            </tr>
                        </tbody>
                    </table>

                    <p>{t("cookiePolicyPage.background.paragraph.5", "Please note that the following third parties may also use cookies, over which we have no control. These named third parties may include, for example, advertising networks and providers of external services like web traffic analysis services. These third party cookies are likely to be analytical cookies or performance cookies or targeting cookies:")}
                        <p>{t("cookiePolicyPage.background.paragraph.6", "We do not use any third party cookies on our app website.")}</p>
                    </p>
                    <p>{t("cookiePolicyPage.background.paragraph.7", "To deactivate the use of third party advertising cookies, you may visit the consumer page to manage the use of these types of cookies.")}</p>
                    <p>{t("cookiePolicyPage.background.paragraph.8", "You can block cookies by activating the setting on your browser that allows you to refuse the setting of all or some cookies. However, if you use your browser settings to block all cookies (including essential cookies) you may not be able to access all or parts of our website.")}</p>
                </div>
            </MainContainer>
        </Background>
        );

}